import { fetchWithTokenRefresh } from "./withTokenRefresh";

const idApiPrefix = "/id/api";
export const postgRESTApiUrl = () => {
  return globalThis.Qwoted?.SUPABASE_API_EXTERNAL_URL;
};

// V1-COMPAT: when this file is copied verbatim into the legacy V1 app, Rails API calls
// (e.g. "/interact/api/messaging/...") must cross origin to V2 instead of staying same-origin.
// No-op here in V2 (V1_HOSTED is false), so behavior is unchanged. Absolute URLs (already
// http*://) and PostgREST URLs (already built from SUPABASE_API_EXTERNAL_URL) are left alone.
export const resolveApiUrl = (url) => {
  if (!globalThis.Qwoted?.V1_HOSTED) return url;
  if (/^https?:\/\//i.test(url)) return url;
  const v2Url = globalThis.Qwoted?.V2_URL;
  return v2Url ? `${v2Url}${url}` : url;
};

export const determineIdOrSlug = (input) => {
  // Check if the input is a numeric string
  if (/^\d+$/.test(input)) {
    return "ID";
  }
  return "Slug";
};

export const determineSelector = (id, { keyId = "id", keySlug = null }) => {
  const idOrSlug = determineIdOrSlug(id);
  if (idOrSlug === "ID") {
    return `${keyId}=eq.${id}`;
  }
  if (!keySlug) throw new Error();
  return `${keySlug}=eq.${id}`;
};

export const determinePaginationParams = (options) => {
  const { page = 1, per_page: perPage = 12, returnType = "string" } = options;

  if (returnType === "object") {
    return { limit: perPage, offset: (page - 1) * perPage };
  }

  return `limit=${perPage}&offset=${(page - 1) * perPage}`;
};

// Full Text Search params with prefix matching
export const determineSearchParamsFts = ({ searchString, fieldsToSearch, partialWordMatch = true }) => {
  const tsQuerySearchString = partialWordMatch ? `${searchString}:*` : searchString;

  if (!searchString || fieldsToSearch.length <= 0) return {};

  if (fieldsToSearch.length === 1) {
    return { [fieldsToSearch[0]]: `fts.${tsQuerySearchString}` };
  }

  const fieldSearchConditions = fieldsToSearch.map((field) => `${field}.fts.${tsQuerySearchString}`);
  const searchParamString = `(${fieldSearchConditions.join(",")})`;
  return { or: searchParamString };
};

// ILIKE search within infix matching
export const determineSearchParams = ({ searchString, fieldsToSearch, partialWordMatch = true }) => {
  const tsQuerySearchString = partialWordMatch ? `*${searchString}*` : searchString;

  if (!searchString || fieldsToSearch.length <= 0) return {};

  if (fieldsToSearch.length === 1) {
    return { [fieldsToSearch[0]]: `ilike.${tsQuerySearchString}` };
  }

  const fieldSearchConditions = fieldsToSearch.map((field) => `${field}.ilike.${tsQuerySearchString}`);
  const searchParamString = `(${fieldSearchConditions.join(",")})`;
  return { or: searchParamString };
};

export const createResourcePath = (objectPath) => {
  return `${idApiPrefix}/${objectPath}`;
};

export const readUrl = (path, { selector = "", paginationParams = "" } = {}) => {
  let url = `${postgRESTApiUrl()}/rest/v1/${path}`;
  if (selector) {
    url += `?${selector}`;
  }
  if (paginationParams) {
    url += selector ? `&${paginationParams}` : `?${paginationParams}`;
  }
  return url;
};

export const readUrlObjectBased = (
  path,
  { selector = undefined, paginationParams = {}, conditionParams = {}, searchParams = {} },
) => {
  const url = `${postgRESTApiUrl()}/rest/v1/${path}`;
  const params = new URLSearchParams({
    ...(selector && { selector }),
    ...paginationParams,
    ...conditionParams,
    ...searchParams,
  });

  if (params.toString() === "") return url;

  return `${url}?${params.toString()}`;
};

export const updateResourcePath = (objectPath, idOrSlug) => {
  return `${idApiPrefix}/${objectPath}/${idOrSlug}`;
};

// Rails may return HTML on 500s (and some other errors). `response.json()` then throws;
// callers must still receive `{ error: ... }` so optimistic UI can roll back / show toasts.
// On parse failure return null; status / statusText are already on the parent error object.
export async function parseResponseBody(response) {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

// createResource/updateResource/deleteResource resolve to `{ data }` on success or `{ error }` on a
// non-2xx — they do NOT throw. A caller that treats "didn't throw" as success will silently mask a
// failed mutation (data loss, no optimistic-UI rollback). throwOnApiError converts that `{ error }`
// shape into a thrown ApiMutationError so a caller's try/catch becomes load-bearing (roll back,
// surface the error). Apply it at the FIRST point the `{ error }` shape exists — before any
// resource-specific unwrapping (e.g. before `response.data.data.attributes`) — otherwise the error is
// already gone. Returns the response unchanged on success so existing unwrapping still works.
export class ApiMutationError extends Error {
  constructor({ status, statusText, body } = {}) {
    super(`API mutation failed (${status} ${statusText})`);
    this.name = "ApiMutationError";
    this.status = status;
    this.statusText = statusText;
    this.body = body;
  }
}

export const throwOnApiError = (response) => {
  if (response?.error) {
    throw new ApiMutationError(response.error);
  }
  return response;
};

// "Create"
export const createResource = async (url, attributes) => {
  const response = await fetchWithTokenRefresh(resolveApiUrl(url), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(attributes),
  });

  const body = await parseResponseBody(response);

  if (response.ok) {
    return { data: body };
  }

  return {
    error: {
      status: response.status,
      statusText: response.statusText,
      body,
    },
  };
};

// Exact row count with no body. HEAD + count=exact returns just the Content-Range header
// (`*/<count>`), so a count badge costs ~zero bytes instead of fetching every matching row.
export const getCount = async (url) => {
  const response = await fetchWithTokenRefresh(resolveApiUrl(url), {
    method: "HEAD",
    headers: { Prefer: "count=exact" },
  });
  if (!response.ok) return 0;
  const contentRange = response.headers?.get("Content-Range");
  const total = contentRange ? parseInt(contentRange.split("/")[1], 10) : NaN;
  return Number.isNaN(total) ? 0 : total;
};

// "Read"
export const getResource = async (url, { signal } = {}) => {
  // Note: count=planned reported a total of 1 for pr_profiles.getAllFullTextSearch when there were 75 rows
  const response = await fetchWithTokenRefresh(resolveApiUrl(url), {
    method: "GET",
    headers: { Prefer: "count=estimated" },
    ...(signal && { signal }),
  });

  const body = await parseResponseBody(response);

  if (response.ok) {
    const contentRange = response.headers?.get("Content-Range");
    let totalRows = 0;
    let remainingRows = 0;

    if (contentRange) {
      const [currentRange, total] = contentRange.split("/");
      totalRows = parseInt(total, 10);
      const endIndex = parseInt(currentRange.split("-")[1], 10);

      if (!Number.isNaN(endIndex)) {
        remainingRows = Math.max(0, totalRows - endIndex - 1);
      }
    }

    return { data: body, totalRows, remainingRows };
  }

  // Handle HTTP Status 416 "Requested Range Not Satisfiable"
  // Since we're using PostgREST with count=estimated, there might be discrepancies in pagination.
  // https://postgrest.org/en/latest/references/api/tables_views.html#estimated-count
  // https://postgrest.org/en/stable/references/errors.html
  const rangeNotSatisfiable = 416;
  if (response.status === rangeNotSatisfiable) {
    return { data: {}, totalRows: null, remainingRows: 0 };
  }

  return {
    error: {
      status: response.status,
      statusText: response.statusText,
      body,
    },
  };
};

// "Update"
export const updateResource = async (url, attributes) => {
  const response = await fetchWithTokenRefresh(resolveApiUrl(url), {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(attributes),
  });

  const body = await parseResponseBody(response);

  if (response.ok) {
    return { data: body };
  }

  return {
    error: {
      status: response.status,
      statusText: response.statusText,
      body,
    },
  };
};

// "Update" with no body -- for actions expressed purely by the verb + URL (e.g. the flip/unflip
// `feature`/`unfeature` endpoints from FlippableRoute), which reject a request body outright.
// Same success/error contract as updateResource/createResource: resolves `{ data }` on 2xx, `{ error }`
// on non-2xx -- never throws, so callers must still apply throwOnApiError.
export const updateResourceBodyless = async (url) => {
  const response = await fetchWithTokenRefresh(resolveApiUrl(url), {
    method: "PUT",
  });

  const body = await parseResponseBody(response);

  if (response.ok) {
    return { data: body };
  }

  return {
    error: {
      status: response.status,
      statusText: response.statusText,
      body,
    },
  };
};

// "Delete"
export const deleteResource = async (url) => {
  const response = await fetchWithTokenRefresh(resolveApiUrl(url), {
    method: "DELETE",
  });

  if (response.ok) {
    return { data: "Resource deleted successfully" };
  }

  const body = await parseResponseBody(response);
  return {
    error: {
      status: response.status,
      statusText: response.statusText,
      body,
    },
  };
};
